import { Field, Label } from "@headlessui/react";
import clsx from "clsx";
import { type HTMLAttributes, useId } from "react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { Checkbox } from "../checkbox";
import type { ChildrenProps } from "../types";

/** Props Interface */
interface FormCheckbox<T extends FieldValues>
	extends Partial<ChildrenProps>,
		HTMLAttributes<HTMLLabelElement> {
	control: Control<T>;
	name: Path<T>;
	isDisabled?: boolean;
	"data-cy"?: string;
}

export function FormCheckbox<T extends FieldValues>({
	control,
	name,
	children,
	isDisabled,
	onChange,
	...props
}: FormCheckbox<T>) {
	const id = useId();

	const { "data-cy": cyId, ...rest } = props;

	/** Render */
	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { onChange, value } }) => (
				<Field className={clsx("flex flex-row items-center", "cursor-pointer")}>
					<Checkbox
						name={id}
						value={value}
						isDisabled={isDisabled}
						onChange={onChange}
						data-cy={cyId}
					/>
					{children && (
						<Label
							className="pl-2 text-color text-sm cursor-pointer select-none"
							htmlFor={id}
							{...rest}
						>
							{children}
						</Label>
					)}
				</Field>
			)}
		/>
	);
}
